import {
  Component,
  OnInit,
  Input,
  Inject,
  ViewChild,
  AfterViewInit,
  Injector,
  Optional,
  Host
} from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { AuthenticationService, ProductValidity } from "src/app/_services";
import { DialogComponent } from "src/app/components/_layout/dialog/dialog.component";
import { LxmDialog } from "src/app/_helpers/dialogs";
import { ActivatedRoute } from "@angular/router";
import {
  ISupplierProductAssortment,
  ISupplierProductAssortmentFormData
} from "./product-store.model";
import { Subscription, forkJoin, Observable } from "rxjs";
import {
  asArray,
  SupplierProductAssortmentStatus,
  Bool,
  UserAction,
  ProductCardSection,
  asObject,
  LxmAppModule
} from "src/app/enum";
import {
  IProductCardFormData,
  IRetailChain,
  IProductCard,
  IProductBaseInfo,
  IProductCardRetailChain
} from "../product-info/product-info.model";
import { nextSchemeDay } from "src/app/_helpers/calculate";
import { AppState } from "src/app/state/app.state";
import { appSettings } from "src/app/app.settings";
import { LxmMessage } from "src/app/_helpers";
import { TranslateService } from "@ngx-translate/core";
import { ProductCardBase } from "../product-card-base";
import { LOCAL_STORAGE_KEY } from "src/app/config";
import { ProductStoreBaseSchemaDialog } from "./dialog/product-store-schema.dialog";
import { ProductStoreService } from "src/app/_services/product-store.service";
import { ProductPricelistCard } from "../product-pricelist/product-pricelist.card";
import { ProductPackagingCard } from "../product-packaging/product-packaging.card";

@Component({
  selector: "product-store-card",
  templateUrl: "./product-store.card.html",
  styleUrls: ["./product-store.card.scss"]
})
export class ProductStoreCard extends ProductCardBase<any[]> implements OnInit {
  public title = "cards.products.product_store_schemes.title";

  public UserAction = UserAction;

  public cardSection = ProductCardSection.StoreSchemes;

  public columns: string[] = [];

  public productCard: IProductCard;
  public storeSchemes: any[];

  public formData: IProductCardFormData;
  public retailChains: IProductCardRetailChain[];
  public retailChainsById: { [key: string]: IProductCardRetailChain };

  @Input()
  public productId: string;

  @Input()
  public packagingCard: ProductPackagingCard;

  public cardValid = true;
  public canEdit: boolean;
  public isOwnProduct: boolean;
  public canEditStoreSchemes: boolean = false;

  private _loadStoreSchemesSubscription: Subscription;

  public isExpandedKey = LOCAL_STORAGE_KEY.PRODUCT_STORE_CARD_OPEN;

  constructor(
    injector: Injector,
    private _appState: AppState,
    private _dialog: LxmDialog,
    private _productStoreService: ProductStoreService,
    route: ActivatedRoute,
    private _authService: AuthenticationService,
    public productValidity: ProductValidity,
    private _msg: LxmMessage
  ) {
    super(
      injector,
      "productSupplierAssortment",
      "productSupplierAssortmentChanged"
    );

    this.productCard = route.snapshot.data.productCard as IProductCard;

    this.isOwnProduct = this.productCard.isOwnProduct;
    this.storeSchemes = this.productCard.storeSchemes;
    this.formData = this.productCard.formData;
    this.retailChains = this.formData.retailChains.filter((x) => !x.isClient);
    this.retailChainsById = this.retailChains.reduce((dict, x) => {
      dict[x.id] = x;
      return dict;
    }, {});
  }

  ngOnInit() {
    super.ngOnInit();

    this.canEdit = this._authService.hasRight([UserAction.ManageProductStoreSchemes]);
    this.canEditStoreSchemes = this._authService.hasModule(LxmAppModule.SellerStore) &&
      this._authService.hasRight([UserAction.ManageProductStoreSchemes]);

    this._setTableColumns();
  }

  public get hasAnyBasePricelistPrice(): boolean {
    const basePrices = this.productCard?.pricesInfo?.supplierBasePrices || [];

    const hasAnyPrice = (prices?: any[]) =>
      Array.isArray(prices) && prices.some((p) => p?.price != null && p?.price !== 0);

    return basePrices.some((x: any) => {
      const hasPublic =
        hasAnyPrice(x?.publicSalesPrices) ||
        (typeof x?.publicSalesPrice === "number" && x.publicSalesPrice !== 0);
      const hasStore =
        hasAnyPrice(x?.storeSalesPrices) ||
        (typeof x?.storeSalesPrice === "number" && x.storeSalesPrice !== 0);

      return hasPublic || hasStore;
    });
  }

  public get canAddNewStoreScheme(): boolean {
    return this.canEditStoreSchemes && this.hasAnyBasePricelistPrice;
  }

  private _setTableColumns() {
    if (this.isOwnProduct) {
      this.columns = ["valid", "visibility", "category", "user"];
    } else {
      this.columns = ["valid", "category", "user"];
    }
  }

  public isAvailable(row: any, chainId: string): boolean {
    if (!row.retailerIds || row.retailerIds.length === 0) {
      return row.retailersListIsExclusive;
    }

    const ix = row.retailerIds?.indexOf(chainId);
    return row.retailersListIsExclusive ? ix < 0 : ix >= 0;
  }

  public openBaseSchemaDialog(row?: any) {
    if ((row && !row.isEditable) || !this.canEdit) {
      return;
    }

    if (!this.canEditStoreSchemes) {
      return;
    }

    // E-store schemes require at least one base pricelist price:
    // standard price OR store price in the general pricelist.
    if (!this.hasAnyBasePricelistPrice) {
      return;
    }

    this._dialog.open(ProductStoreBaseSchemaDialog, {
      ...appSettings.DIALOG_SIZES.M,
      data: {
        storeSchemesCard: this,
        retailChains: this.retailChains,
        schema: row,
        packagingCard: this.packagingCard,
        hasAnyScheme: this.storeSchemes.length > 0
      },
      resolve: () => {
        const resolvers: any = {
          formData: this._productStoreService.getFormData(this.productId)
        };
        return forkJoin(resolvers);
      }
    });
  }

  public loadStoreSchemes(): void {
    if (this._loadStoreSchemesSubscription) {
      this._loadStoreSchemesSubscription.unsubscribe();
    }

    this._loadStoreSchemesSubscription = this._productStoreService
      .getGridData(this.productId)
      .subscribe(
        (result) => {
          this.storeSchemes = result;
        },
        (err) => {
          console.error(err);
        }
      );
  }
}
